import React, { useMemo } from 'react';
import { Box, Typography, CircularProgress } from 'extended-oxygen-elements';
import { DutyConfiguration } from '../types';
import { DutyRow } from './DutyRow';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';

// Inline constants to avoid module resolution issues
const COLORS = {
  primary: '#000000',
  borderLight: 'rgba(0, 0, 0, 0.08)',
  borderMedium: 'rgba(0, 0, 0, 0.12)',
} as const;

type DutiesTableProps = {
  duties: DutyConfiguration[];
  isLoading: boolean;
  onConfigure: (dutyType: string) => void;
};

// Icon components mapped by key
const ICON_COMPONENTS = {
  VpnKeyIcon,
  MedicalServicesIcon,
  LocalFireDepartmentIcon,
  HealthAndSafetyIcon,
} as const;

// Exact name matches for duty icons (highest priority)
const ICON_BY_NAME: Record<string, keyof typeof ICON_COMPONENTS> = {
  'Key Holder': 'VpnKeyIcon',
  'First Aid Officer': 'MedicalServicesIcon',
  'Fire Warden': 'LocalFireDepartmentIcon',
  'Health & Safety Officer': 'HealthAndSafetyIcon',
  'Mental Health First Aider': 'MedicalServicesIcon',
};

// Pattern-based fallback for icon matching
const ICON_PATTERNS: Array<{ pattern: RegExp; icon: keyof typeof ICON_COMPONENTS }> = [
  { pattern: /key/i, icon: 'VpnKeyIcon' },
  { pattern: /(first aid|medical|mental health)/i, icon: 'MedicalServicesIcon' },
  { pattern: /fire/i, icon: 'LocalFireDepartmentIcon' },
  { pattern: /(health|safety)/i, icon: 'HealthAndSafetyIcon' },
];

const getDutyIcon = (duty: DutyConfiguration): React.ComponentType<{ sx?: any }> | undefined => {
  const name = duty.duty_name || duty.duty_type;
  
  // Try exact match first
  if (ICON_BY_NAME[name]) {
    return ICON_COMPONENTS[ICON_BY_NAME[name]];
  }
  
  // Fallback to pattern matching
  const lowerName = name.toLowerCase();
  const match = ICON_PATTERNS.find(({ pattern }) => pattern.test(lowerName));
  return match ? ICON_COMPONENTS[match.icon] : undefined;
};

export function DutiesTable({ duties, isLoading, onConfigure }: DutiesTableProps) {
  const dutiesWithIcons = useMemo(
    () => duties.map(duty => ({ duty, icon: getDutyIcon(duty) })),
    [duties]
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
        }}
        role="status"
        aria-live="polite"
      >
        <CircularProgress aria-label="Loading duties" />
      </Box>
    );
  }

  if (!duties || duties.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
        }}
        role="status"
        aria-live="polite"
      >
        <Typography variant="body1" color="textSecondary">
          No duty configurations found for this location and day.
        </Typography>
      </Box>
    );
  }

  return (
    <Box role="table" aria-label="Duties configuration table">
      {/* Desktop Header - Hidden on Mobile */}
      <Box
        role="rowgroup"
        sx={{
          display: { xs: 'none', sm: 'flex' },
          gap: '12px',
          alignItems: 'center',
          padding: '8px 0',
          borderBottom: `1px solid ${COLORS.borderMedium}`,
        }}
      >
        <Box sx={{ flexShrink: 0, width: '24px' }} /> {/* Checkbox space */}
        <Box role="columnheader" sx={{ flex: '1 0 0', minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.primary }}>
            Duty
          </Typography>
        </Box>
        <Box role="columnheader" sx={{ width: '180px', flexShrink: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.primary }}>
            Times / No. People
          </Typography>
        </Box>
        <Box role="columnheader" sx={{ width: '100px', flexShrink: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.primary }}>
            No. People
          </Typography>
        </Box>
        <Box role="columnheader" sx={{ width: '120px', flexShrink: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.primary }}>
            Configure
          </Typography>
        </Box>
      </Box>

      {/* Rows */}
      <Box role="rowgroup">
        {dutiesWithIcons.map(({ duty, icon }, index) => (
          <React.Fragment key={duty.duty_type}>
            <DutyRow
              duty={duty}
              onConfigure={onConfigure}
              icon={icon}
            />
            {/* Desktop divider - only between rows */}
            {index < dutiesWithIcons.length - 1 && (
              <Box
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  borderBottom: `1px solid ${COLORS.borderLight}`,
                }}
                aria-hidden="true"
              />
            )}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}

export default DutiesTable;
